import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { useApp } from '../context/AppContext.js';
import { ensureValidSession } from '../utils/auth.js';
import LoadingFallback from './LoadingFallback.js';

// Keep critical components for initial load
import WrongAnswerDialog from './common/WrongAnswerDialog.js';
import SettingsDialog from '../pages/settings/SettingsDialog.js';
import ClienLayout from './ClienLayout.js';
import MobileLayout from './MobileLayout.js';
import { useDeviceType } from '../hooks/useDeviceType';

// Lazy load all page components
const QuizContent = lazy(() => import('./QuizContent.js'));
const Login = lazy(() => import('./auth/Login.js'));
const SignUp = lazy(() => import('./auth/SignUp.js'));
const LandingPage = lazy(() => import('./LandingPage.js'));
const MainPage = lazy(() => import('./MainPage.js'));
const ProfileSettings = lazy(() => import('../pages/settings/ProfileSettings.js'));
const Pricing = lazy(() => import('../pages/Pricing.js'));
const SettingsPage = lazy(() => import('../pages/SettingsPage.js'));
const SuccessStoryDetailPage = lazy(() => import('../pages/community/success-stories/[storyId].js'));
const SuccessStoryEdit = lazy(() => import('../pages/community/success-stories/SuccessStoryEdit.js'));
const SuccessStoryForm = lazy(() => import('../pages/community/success-stories/SuccessStoryForm.js'));
const SuccessStoriesPage = lazy(() => import('../pages/community/success-stories/index.js'));
const StudyGroupsPage = lazy(() => import('../pages/community/study-groups/index.js'));
const FreeBoardPage = lazy(() => import('../pages/community/free-board/index.js'));
const QnaPage = lazy(() => import('../pages/community/casebrief/index.js'));
const StudyTipsPage = lazy(() => import('../pages/community/study-tips/index.js'));
const MbeStudyMainPage = lazy(() => import('../pages/community/mbe-study/index.js'));
const MbeStudyPanePage = lazy(() => import('../pages/community/mbe-study/StudyPane.js'));
const FreeBoardDetail = lazy(() => import('../pages/community/free-board/[postId].js'));
const MptTestMainPage = lazy(() => import('../pages/mpt_test/index.js'));
const CaseBriefDetail = lazy(() => import('../pages/community/casebrief/[id].js'));
const MbeTestMainPage = lazy(() => import('../pages/mbe_test/index.js'));
const TestPage = lazy(() => import('../pages/mbe_test/TestPage.js'));
const MbeAdminMainPage = lazy(() => import('../pages/mbe_admin/index.js'));
const AdminLogsPage = lazy(() => import('../pages/admin/logs/index.js'));
const AdminRoadmapPage = lazy(() => import('../pages/admin/roadmap/index.js'));
const MeeTestMainPage = lazy(() => import('../pages/mee_test/index.js'));
const EvaluatorDashboard = lazy(() => import('../pages/evaluator/index.js'));
const EvaluationDetailPage = lazy(() => import('../pages/evaluator/EvaluationDetailPage.js'));
const UserManagement = lazy(() => import('../components/UserManagement.js'));
const MyLearningPage = lazy(() => import('../pages/my-learning/index.js'));
const MyReviewPage = lazy(() => import('../pages/my_review/index.js'));
const CommunityPage = lazy(() => import('../pages/community/index.js'));
const NoticesPage = lazy(() => import('../pages/community/notices/index.js'));
const LegalDictionaryPage = lazy(() => import('../pages/community/legal-dictionary/index.js'));
const LegalTermDetailPage = lazy(() => import('../pages/community/legal-dictionary/[termId].js'));
const SearchPage = lazy(() => import('../pages/SearchPage.js'));


const MainContent = () => {
  const {
    isLoggedIn,
    isAdmin,
    user,
    signup,
    logout,
    isEvaluator,
    loading: isAuthLoading,
    userProfile,
    signupSuccessDialog,
    setSignupSuccessDialog
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { isMobileApp, isPWA } = useDeviceType();
  const {
    openSettings,
    setOpenSettings,
    openWrongAnswers,
    setOpenWrongAnswers,
    isLoading: isAppLoading
  } = useApp();

  // 전역 세션 상태 관리
  useEffect(() => {
    if (isLoggedIn && user) {
      // 페이지 로드 시 세션 유효성 검사
      const checkInitialSession = async () => {
        const session = await ensureValidSession();
        if (!session) {
          logout();
        }
      };

      checkInitialSession();

      // 주기적 세션 검증 (10분마다)
      const sessionCheckInterval = setInterval(async () => {
        const session = await ensureValidSession();
        if (!session) {
          logout();
        }
      }, 10 * 60 * 1000); // 10분마다 확인

      return () => clearInterval(sessionCheckInterval);
    }
  }, [isLoggedIn, user, logout]);

  // 페이지 가시성 변경 시 세션 검증 (탭 전환 등)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isLoggedIn && user) {
        const session = await ensureValidSession();
        if (!session) {
          logout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isLoggedIn, user, logout]);

  const handleWrongAnswersClose = () => {
    setOpenWrongAnswers(false);
  };

  const handleSettingsClose = () => {
    setOpenSettings(false);
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleLoginClose = () => {
    // Navigate to home instead of going back to prevent blank page
    navigate('/');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleSignupClose = () => {
    // Navigate to home instead of going back to prevent blank page
    navigate('/');
  };

  const handleWrongAnswersClick = () => {
    navigate('/my-learning');
  };

  // 로그인 상태 변경 시 리다이렉트 처리
  useEffect(() => {
    if (isLoggedIn && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup')) {
      navigate('/');
    }
  }, [isLoggedIn, location.pathname, navigate]);

  // PWA standalone 모드 최초 진입 시 스마트 리다이렉션 처리
  useEffect(() => {
    if (isPWA && !isLoggedIn) {
      // PWA 비회원은 불필요한 랜딩페이지 소개를 건너뛰고 즉시 로그인 화면으로 직행하여 네이티브 앱 느낌 부여
      if (location.pathname === '/' || location.pathname === '/pricing') {
        navigate('/login', { replace: true });
      }
    }
  }, [isPWA, isLoggedIn, location.pathname, navigate]);

  // isEvaluator 값 확인을 위한 로그 추가
  useEffect(() => {
  }, [isLoggedIn, isEvaluator, user]);

  // Show a global loader while authentication or essential app data is loading
  return (
    <>
      {/* 랜딩페이지, 로그인/회원가입, 요금제 페이지는 별도 레이아웃 */}
      {!isLoggedIn && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/pricing') ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <Box component="main" sx={{ flexGrow: 1 }}>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <LandingPage onLoginClick={handleLoginClick} onSignupClick={handleSignupClick} />
                  }
                />
                <Route path="/login" element={<Login open={location.pathname === '/login'} onClose={handleLoginClose} />} />
                <Route path="/signup" element={
                  <SignUp
                    open={location.pathname === '/signup'}
                    onClose={handleSignupClose}
                    onSignUp={signup}
                  />
                } />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Box>
        </Box>
      ) : isMobileApp ? (
        /* 모바일/PWA 전용 레이아웃 (PC와 완벽히 분리) */
        <MobileLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<MainPage />} />
              <Route path="/bll-learning" element={<Box sx={{ width: '100%', height: '100%', pt: 2 }}><QuizContent /></Box>} />
              <Route path="/user-management" element={isAdmin ? <Box sx={{ width: '100%', height: '100%', pt: 2 }}><UserManagement /></Box> : <Navigate to="/" replace />} />
              <Route path="/login" element={<Login open={location.pathname === '/login'} onClose={handleLoginClose} />} />
              <Route path="/signup" element={<SignUp open={location.pathname === '/signup'} onClose={handleSignupClose} onSignUp={signup} />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/settings" element={isLoggedIn ? <SettingsPage /> : <Navigate to="/" replace />} />
              <Route path="/settings/profile" element={isLoggedIn ? <ProfileSettings /> : <Navigate to="/" replace />} />
              <Route path="/my-learning" element={isLoggedIn ? <MyLearningPage /> : <Navigate to="/" replace />} />
              <Route path="/wrong-answers" element={isLoggedIn ? <Navigate to="/my-learning" replace /> : <Navigate to="/" replace />} />

              {/* 커뮤니티 관련 라우트 */}
              <Route path="/search" element={<SearchPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/notices" element={<NoticesPage />} />
              <Route path="/community/legal-dictionary" element={<LegalDictionaryPage />} />
              <Route path="/community/legal-dictionary/:termId" element={<LegalTermDetailPage />} />
              <Route path="/community/success-stories" element={<SuccessStoriesPage />} />
              <Route path="/community/success-stories/:storyId" element={<SuccessStoryDetailPage />} />
              <Route path="/community/success-stories/:storyId/edit" element={<SuccessStoryEdit />} />
              <Route path="/community/success-stories/new" element={<SuccessStoryForm />} />
              <Route path="/community/study-groups/*" element={<StudyGroupsPage />} />
              <Route path="/community/free-board" element={<FreeBoardPage />} />
              <Route path="/community/casebrief" element={<QnaPage />} />
              <Route path="/community/study-tips" element={<StudyTipsPage />} />
              <Route path="/community/study-tips/:subjectId" element={<StudyTipsPage />} />
              <Route path="/community/mbe-study" element={<MbeStudyMainPage />} />
              <Route path="/community/mbe-study/:subjectId" element={<MbeStudyPanePage />} />
              <Route path="/community/free-board/:postId" element={<FreeBoardDetail />} />
              <Route path="/community/casebrief/:id" element={<CaseBriefDetail />} />

              {/* MPT 실전 테스트 라우트 */}
              <Route path="/mpt_test" element={<MptTestMainPage />} />

              {/* MEE 실전 테스트 라우트 */}
              <Route path="/mee_test" element={<MeeTestMainPage />} />

              {/* MBE 실전 테스트 라우트 */}
              <Route path="/mbe_test" element={<MbeTestMainPage />} />
              <Route path="/mbe_test/test" element={<TestPage />} />
              
              {/* MBE 관리 테스트 라우트 */}
              <Route path="/mbe_admin" element={isAdmin ? <MbeAdminMainPage /> : <Navigate to="/" replace />} />
              <Route path="/admin/logs" element={isAdmin ? <AdminLogsPage /> : <Navigate to="/" replace />} />
              <Route path="/admin/roadmap" element={isAdmin ? <AdminRoadmapPage /> : <Navigate to="/" replace />} />

              {/* 복습/비교 라우트 */}
              <Route path="/my_review" element={<MyReviewPage />} />

              {/* 평가자 전용 라우트 */}
              <Route path="/evaluator" element={isEvaluator ? <EvaluatorDashboard /> : <Navigate to="/" replace />} />
              <Route path="/evaluator/evaluate/:answerType/:answerId" element={isEvaluator ? <EvaluationDetailPage /> : <Navigate to="/" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </MobileLayout>
      ) : (
        /* 클리앙 스타일 레이아웃 적용 (PC 전용) */
        <ClienLayout>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route
                path="/"
                element={
                  <MainPage />
                }
              />
              <Route
                path="/bll-learning"
                element={
                  <Box sx={{ width: '100%', height: '100%', pt: 2 }}>
                    <QuizContent />
                  </Box>
                }
              />
              <Route
                path="/user-management"
                element={
                  isAdmin ? (
                    <Box sx={{ width: '100%', height: '100%', pt: 2 }}>
                      <UserManagement />
                    </Box>
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
              <Route path="/login" element={<Login open={location.pathname === '/login'} onClose={handleLoginClose} />} />
              <Route path="/signup" element={
                <SignUp
                  open={location.pathname === '/signup'}
                  onClose={handleSignupClose}
                  onSignUp={signup}
                />
              } />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/settings" element={
                isLoggedIn ? <SettingsPage /> : <Navigate to="/" replace />
              } />
              <Route path="/settings/profile" element={
                isLoggedIn ? <ProfileSettings /> : <Navigate to="/" replace />
              } />
              <Route path="/my-learning" element={
                isLoggedIn ? <MyLearningPage /> : <Navigate to="/" replace />
              } />
              <Route path="/wrong-answers" element={
                isLoggedIn ? <Navigate to="/my-learning" replace /> : <Navigate to="/" replace />
              } />

              {/* 커뮤니티 관련 라우트 */}
              <Route path="/search" element={<SearchPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/notices" element={<NoticesPage />} />
              <Route path="/community/legal-dictionary" element={<LegalDictionaryPage />} />
              <Route path="/community/legal-dictionary/:termId" element={<LegalTermDetailPage />} />
              <Route path="/community/success-stories" element={<SuccessStoriesPage />} />
              <Route path="/community/success-stories/:storyId" element={<SuccessStoryDetailPage />} />
              <Route path="/community/success-stories/:storyId/edit" element={<SuccessStoryEdit />} />
              <Route path="/community/success-stories/new" element={<SuccessStoryForm />} />
              <Route path="/community/study-groups/*" element={<StudyGroupsPage />} />
              <Route path="/community/free-board" element={<FreeBoardPage />} />
              <Route path="/community/casebrief" element={<QnaPage />} />
              <Route path="/community/study-tips" element={<StudyTipsPage />} />
              <Route path="/community/study-tips/:subjectId" element={<StudyTipsPage />} />
              <Route path="/community/mbe-study" element={<MbeStudyMainPage />} />
              <Route path="/community/mbe-study/:subjectId" element={<MbeStudyPanePage />} />
              <Route path="/community/free-board/:postId" element={<FreeBoardDetail />} />
              <Route path="/community/casebrief/:id" element={<CaseBriefDetail />} />

              {/* MPT 실전 테스트 라우트 */}
              <Route path="/mpt_test" element={<MptTestMainPage />} />

              {/* MEE 실전 테스트 라우트 */}
              <Route path="/mee_test" element={<MeeTestMainPage />} />

              {/* MBE 실전 테스트 라우트 */}
              <Route path="/mbe_test" element={<MbeTestMainPage />} />
              <Route path="/mbe_test/test" element={<TestPage />} />

              {/* MBE 관리 테스트 라우트 */}
              <Route path="/mbe_admin" element={
                isAdmin ? <MbeAdminMainPage /> : <Navigate to="/" replace />
              } />
              <Route path="/admin/logs" element={
                isAdmin ? <AdminLogsPage /> : <Navigate to="/" replace />
              } />
              <Route path="/admin/roadmap" element={
                isAdmin ? <AdminRoadmapPage /> : <Navigate to="/" replace />
              } />

              {/* 복습/비교 라우트 */}
              <Route path="/my_review" element={<MyReviewPage />} />

              {/* 평가자 전용 라우트 */}
              <Route path="/evaluator" element={
                isEvaluator ? <EvaluatorDashboard /> : <Navigate to="/" replace />
              } />
              <Route path="/evaluator/evaluate/:answerType/:answerId" element={
                isEvaluator ? <EvaluationDetailPage /> : <Navigate to="/" replace />
              } />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </ClienLayout>
      )}
      <WrongAnswerDialog open={openWrongAnswers} onClose={handleWrongAnswersClose} />
      <SettingsDialog open={openSettings} onClose={handleSettingsClose} />

      {/* 회원가입 완료 안내 다이얼로그 */}
      <Dialog
        open={signupSuccessDialog}
        onClose={() => setSignupSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '1.5rem',
          py: 2
        }}>
          회원가입 신청 완료
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium', mb: 1 }}>
            회원가입 절차가 다음과 같이 진행됩니다:
          </Typography>
          <Box component="ol" sx={{ ml: 2, mb: 2 }}>
            <Typography component="li" sx={{ mb: 0.5 }}>
              입력하신 이메일로 인증 메일이 발송되었습니다.
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              이메일에 포함된 링크를 클릭하여 이메일 인증을 완료해 주세요.
            </Typography>
            <Typography component="li" sx={{ mb: 0.5 }}>
              관리자 승인 후 로그인이 가능합니다.
            </Typography>
          </Box>

          <Typography
            variant="subtitle1"
            sx={{
              bgcolor: 'warning.light',
              color: 'warning.dark',
              p: 1.5,
              borderRadius: 1,
              fontWeight: 'medium',
              mb: 1
            }}
          >
            ⚠️ 이메일이 스팸메일함에 있을 수 있으니 확인해 주세요!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'center' }}>
          <Button
            onClick={() => setSignupSuccessDialog(false)}
            variant="contained"
            size="large"
            sx={{ minWidth: 100 }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MainContent;
